/**
 * Operation type definitions for the workflow designer
 *
 * These types define the available operations (triggers, actions, control flow)
 * that can be added to a workflow.
 */

/**
 * Category of an operation
 */
export type OperationCategory = 'trigger' | 'action' | 'control';

/**
 * Definition of an available operation in the designer
 */
export interface OperationDefinition {
  /** Unique identifier for the operation */
  id: string;
  /** Display name shown in the UI */
  name: string;
  /** The type value used in workflow JSON (e.g., "Http", "ApiConnection", "If") */
  type: string;
  /** Optional kind value (e.g., "Http" for triggers) */
  kind?: string;
  /** Category: trigger, action, or control flow */
  category: OperationCategory;
  /** Description shown in the operation picker */
  description?: string;
  /** Icon URL or Fluent icon name */
  icon?: string;
  /** Brand color for the operation (hex) */
  brandColor?: string;
  /**
   * Default inputs when creating the operation.
   * Following LogicAppsUX pattern:
   * - For most operations: object with property defaults (e.g., { content: '', schema: {} })
   * - For Compose-type operations: direct value (e.g., '' for empty string)
   * The value is assigned to action.inputs as-is.
   */
  defaultInputs?: unknown;
  /** Whether this is a connector operation (requires connection) */
  isConnector?: boolean;
  /** Connector ID if this is a connector operation */
  connectorId?: string;
  /** Connector display name if this is a connector operation */
  connectorName?: string;
  /** Swagger path template for path parameter extraction (e.g., "/users/{id}/messages/{messageId}") */
  swaggerPathTemplate?: string;
  /** Fuse.js search relevance score (0 = perfect match, 1 = worst match). Only set during search. */
  searchScore?: number;
}

/**
 * Built-in trigger operations
 */
export const BUILTIN_TRIGGERS: OperationDefinition[] = [
  {
    id: 'manual',
    name: 'When an HTTP request is received',
    type: 'Request',
    kind: 'Http',
    category: 'trigger',
    description: 'Trigger the workflow when an HTTP request is received',
    brandColor: '#009900',
    defaultInputs: {
      schema: {},
    },
  },
  {
    id: 'recurrence',
    name: 'Recurrence',
    type: 'Recurrence',
    category: 'trigger',
    description: 'Trigger the workflow on a recurring schedule',
    brandColor: '#009900',
    defaultInputs: {
      frequency: 'Day',
      interval: 1,
    },
  },
  {
    id: 'service_bus_queue_trigger',
    name: 'When messages are available in a queue',
    type: 'ServiceProvider',
    kind: 'ServiceBus',
    category: 'trigger',
    description: 'Trigger when messages arrive in a Service Bus queue',
    brandColor: '#C4D600',
    connectorName: 'Service Bus',
    isConnector: true,
    defaultInputs: {
      queueName: '',
    },
  },
  {
    id: 'service_bus_topic_trigger',
    name: 'When messages are available in a topic',
    type: 'ServiceProvider',
    kind: 'ServiceBus',
    category: 'trigger',
    description: 'Trigger when messages arrive in a Service Bus topic',
    brandColor: '#C4D600',
    connectorName: 'Service Bus',
    isConnector: true,
    defaultInputs: {
      topicName: '',
      subscriptionName: '',
    },
  },
  {
    id: 'blob_trigger',
    name: 'When a blob is added or modified',
    type: 'ServiceProvider',
    kind: 'AzureBlob',
    category: 'trigger',
    description: 'Trigger when a blob is added or modified in a container',
    brandColor: '#0078D4',
    connectorName: 'Azure Blob Storage',
    isConnector: true,
    defaultInputs: {
      path: '',
    },
  },
  {
    id: 'event_grid_trigger',
    name: 'When a resource event occurs',
    type: 'ServiceProvider',
    kind: 'EventGrid',
    category: 'trigger',
    description: 'Trigger on Azure Event Grid events',
    brandColor: '#0078D4',
    connectorName: 'Azure Event Grid',
    isConnector: true,
    defaultInputs: {},
  },
  {
    id: 'event_hub_trigger',
    name: 'When events are available in Event Hub',
    type: 'ServiceProvider',
    kind: 'EventHub',
    category: 'trigger',
    description: 'Trigger when events arrive in Event Hub',
    brandColor: '#0078D4',
    connectorName: 'Azure Event Hubs',
    isConnector: true,
    defaultInputs: {
      eventHubName: '',
    },
  },
  {
    id: 'storage_queue_trigger',
    name: 'When there are messages in a queue',
    type: 'ServiceProvider',
    kind: 'AzureQueues',
    category: 'trigger',
    description: 'Trigger when messages arrive in Storage Queue',
    brandColor: '#0078D4',
    connectorName: 'Azure Queue Storage',
    isConnector: true,
    defaultInputs: {
      queueName: '',
    },
  },
];

/**
 * Built-in action operations (non-connector)
 */
export const BUILTIN_ACTIONS: OperationDefinition[] = [
  // Data Operations
  {
    id: 'http',
    name: 'HTTP',
    type: 'Http',
    category: 'action',
    description: 'Make an HTTP request to any endpoint',
    brandColor: '#709727',
    defaultInputs: {
      method: 'GET',
      uri: '',
    },
  },
  {
    id: 'compose',
    name: 'Compose',
    type: 'Compose',
    category: 'action',
    description: 'Compose a value or transform data',
    brandColor: '#8C6CFF',
    defaultInputs: '',  // Compose inputs is a direct value, not an object
  },
  {
    id: 'parse_json',
    name: 'Parse JSON',
    type: 'ParseJson',
    category: 'action',
    description: 'Parse JSON content into a structured object',
    brandColor: '#8C6CFF',
    defaultInputs: {
      content: '',
      schema: {},
    },
  },
  {
    id: 'response',
    name: 'Response',
    type: 'Response',
    category: 'action',
    description: 'Return a response to the caller',
    brandColor: '#009900',
    defaultInputs: {
      statusCode: 200,
      body: '',
    },
  },
  {
    id: 'terminate',
    name: 'Terminate',
    type: 'Terminate',
    category: 'action',
    description: 'Stop the workflow and set its status',
    brandColor: '#FF4444',
    defaultInputs: {
      runStatus: 'Succeeded',
    },
  },
  {
    id: 'delay',
    name: 'Delay',
    type: 'Wait',
    category: 'action',
    description: 'Pause the workflow for a specified duration',
    brandColor: '#8C6CFF',
    defaultInputs: {
      interval: {
        count: 1,
        unit: 'Minute',
      },
    },
  },
  {
    id: 'set_variable',
    name: 'Set variable',
    type: 'SetVariable',
    category: 'action',
    description: 'Set the value of a variable',
    brandColor: '#FF4444',
    defaultInputs: {
      name: '',
      value: '',
    },
  },
  {
    id: 'init_variable',
    name: 'Initialize variable',
    type: 'InitializeVariable',
    category: 'action',
    description: 'Create and initialize a new variable',
    brandColor: '#FF4444',
    defaultInputs: {
      variables: [
        {
          name: '',
          type: 'String',
          value: '',
        },
      ],
    },
  },
  {
    id: 'append_to_string',
    name: 'Append to string variable',
    type: 'AppendToStringVariable',
    category: 'action',
    description: 'Append text to a string variable',
    brandColor: '#FF4444',
    defaultInputs: {
      name: '',
      value: '',
    },
  },
  {
    id: 'append_to_array',
    name: 'Append to array variable',
    type: 'AppendToArrayVariable',
    category: 'action',
    description: 'Append an item to an array variable',
    brandColor: '#FF4444',
    defaultInputs: {
      name: '',
      value: '',
    },
  },
  {
    id: 'increment_variable',
    name: 'Increment variable',
    type: 'IncrementVariable',
    category: 'action',
    description: 'Increment the value of a numeric variable',
    brandColor: '#FF4444',
    defaultInputs: {
      name: '',
      value: 1,
    },
  },
  // Azure Services
  {
    id: 'service_bus_send',
    name: 'Send message',
    type: 'ServiceProvider',
    kind: 'ServiceBus',
    category: 'action',
    description: 'Send a message to a Service Bus queue or topic',
    brandColor: '#C4D600',
    connectorName: 'Service Bus',
    isConnector: true,
    defaultInputs: {
      entityName: '',
      message: '',
    },
  },
  {
    id: 'service_bus_complete',
    name: 'Complete the message',
    type: 'ServiceProvider',
    kind: 'ServiceBus',
    category: 'action',
    description: 'Complete a Service Bus message',
    brandColor: '#C4D600',
    connectorName: 'Service Bus',
    isConnector: true,
    defaultInputs: {},
  },
  {
    id: 'blob_create',
    name: 'Create blob',
    type: 'ServiceProvider',
    kind: 'AzureBlob',
    category: 'action',
    description: 'Create a new blob in Azure Storage',
    brandColor: '#0078D4',
    connectorName: 'Azure Blob Storage',
    isConnector: true,
    defaultInputs: {
      path: '',
      content: '',
    },
  },
  {
    id: 'blob_read',
    name: 'Read blob content',
    type: 'ServiceProvider',
    kind: 'AzureBlob',
    category: 'action',
    description: 'Read content from an Azure blob',
    brandColor: '#0078D4',
    connectorName: 'Azure Blob Storage',
    isConnector: true,
    defaultInputs: {
      path: '',
    },
  },
  {
    id: 'blob_delete',
    name: 'Delete blob',
    type: 'ServiceProvider',
    kind: 'AzureBlob',
    category: 'action',
    description: 'Delete a blob from Azure Storage',
    brandColor: '#0078D4',
    connectorName: 'Azure Blob Storage',
    isConnector: true,
    defaultInputs: {
      path: '',
    },
  },
  {
    id: 'blob_list',
    name: 'List blobs',
    type: 'ServiceProvider',
    kind: 'AzureBlob',
    category: 'action',
    description: 'List blobs in a container',
    brandColor: '#0078D4',
    connectorName: 'Azure Blob Storage',
    isConnector: true,
    defaultInputs: {
      path: '',
    },
  },
  {
    id: 'table_insert',
    name: 'Insert entity',
    type: 'ServiceProvider',
    kind: 'AzureTableStorage',
    category: 'action',
    description: 'Insert an entity into Azure Table Storage',
    brandColor: '#0078D4',
    connectorName: 'Azure Table Storage',
    isConnector: true,
    defaultInputs: {
      tableName: '',
      entity: {},
    },
  },
  {
    id: 'queue_send',
    name: 'Put a message',
    type: 'ServiceProvider',
    kind: 'AzureQueues',
    category: 'action',
    description: 'Send a message to Azure Queue Storage',
    brandColor: '#0078D4',
    connectorName: 'Azure Queue Storage',
    isConnector: true,
    defaultInputs: {
      queueName: '',
      message: '',
    },
  },
  {
    id: 'event_hub_send',
    name: 'Send event',
    type: 'ServiceProvider',
    kind: 'EventHub',
    category: 'action',
    description: 'Send events to Azure Event Hubs',
    brandColor: '#0078D4',
    connectorName: 'Azure Event Hubs',
    isConnector: true,
    defaultInputs: {
      eventHubName: '',
      content: '',
    },
  },
  {
    id: 'cosmos_create',
    name: 'Create or update document',
    type: 'ServiceProvider',
    kind: 'AzureCosmosDB',
    category: 'action',
    description: 'Create or update a document in Cosmos DB',
    brandColor: '#0078D4',
    connectorName: 'Azure Cosmos DB',
    isConnector: true,
    defaultInputs: {
      databaseId: '',
      containerId: '',
      document: {},
    },
  },
  {
    id: 'cosmos_read',
    name: 'Read document',
    type: 'ServiceProvider',
    kind: 'AzureCosmosDB',
    category: 'action',
    description: 'Read a document from Cosmos DB',
    brandColor: '#0078D4',
    connectorName: 'Azure Cosmos DB',
    isConnector: true,
    defaultInputs: {
      databaseId: '',
      containerId: '',
      id: '',
    },
  },
  {
    id: 'cosmos_query',
    name: 'Query documents',
    type: 'ServiceProvider',
    kind: 'AzureCosmosDB',
    category: 'action',
    description: 'Query documents from Cosmos DB',
    brandColor: '#0078D4',
    connectorName: 'Azure Cosmos DB',
    isConnector: true,
    defaultInputs: {
      databaseId: '',
      containerId: '',
      query: '',
    },
  },
  {
    id: 'sql_execute_query',
    name: 'Execute a SQL query',
    type: 'ServiceProvider',
    kind: 'Sql',
    category: 'action',
    description: 'Execute a SQL query and get results',
    brandColor: '#CC2131',
    connectorName: 'SQL Server',
    isConnector: true,
    defaultInputs: {
      query: '',
    },
  },
  {
    id: 'sql_insert_row',
    name: 'Insert row',
    type: 'ServiceProvider',
    kind: 'Sql',
    category: 'action',
    description: 'Insert a row into a SQL table',
    brandColor: '#CC2131',
    connectorName: 'SQL Server',
    isConnector: true,
    defaultInputs: {
      tableName: '',
      row: {},
    },
  },
  // Third-party connectors
  {
    id: 'outlook_send_email',
    name: 'Send an email',
    type: 'ApiConnection',
    kind: 'V1',
    category: 'action',
    description: 'Send an email using Office 365 Outlook',
    brandColor: '#0078D4',
    connectorName: 'Office 365 Outlook',
    isConnector: true,
    defaultInputs: {
      to: '',
      subject: '',
      body: '',
    },
  },
  {
    id: 'teams_post_message',
    name: 'Post message in a chat or channel',
    type: 'ApiConnection',
    kind: 'V1',
    category: 'action',
    description: 'Post a message to Microsoft Teams',
    brandColor: '#464EB8',
    connectorName: 'Microsoft Teams',
    isConnector: true,
    defaultInputs: {
      teamId: '',
      channelId: '',
      message: '',
    },
  },
  {
    id: 'sharepoint_create_item',
    name: 'Create item',
    type: 'ApiConnection',
    kind: 'V1',
    category: 'action',
    description: 'Create a new item in SharePoint list',
    brandColor: '#036C70',
    connectorName: 'SharePoint',
    isConnector: true,
    defaultInputs: {
      siteId: '',
      listId: '',
      item: {},
    },
  },
  {
    id: 'sharepoint_get_items',
    name: 'Get items',
    type: 'ApiConnection',
    kind: 'V1',
    category: 'action',
    description: 'Get items from a SharePoint list',
    brandColor: '#036C70',
    connectorName: 'SharePoint',
    isConnector: true,
    defaultInputs: {
      siteId: '',
      listId: '',
    },
  },
  {
    id: 'onedrive_create_file',
    name: 'Create file',
    type: 'ApiConnection',
    kind: 'V1',
    category: 'action',
    description: 'Create a file in OneDrive',
    brandColor: '#0078D4',
    connectorName: 'OneDrive for Business',
    isConnector: true,
    defaultInputs: {
      path: '',
      content: '',
    },
  },
  {
    id: 'slack_post_message',
    name: 'Post message',
    type: 'ApiConnection',
    kind: 'V1',
    category: 'action',
    description: 'Post a message to a Slack channel',
    brandColor: '#4A154B',
    connectorName: 'Slack',
    isConnector: true,
    defaultInputs: {
      channel: '',
      text: '',
    },
  },
  {
    id: 'dataverse_create',
    name: 'Add a new row',
    type: 'ApiConnection',
    kind: 'V1',
    category: 'action',
    description: 'Add a new row to a Dataverse table',
    brandColor: '#2E7D32',
    connectorName: 'Dataverse',
    isConnector: true,
    defaultInputs: {
      tableName: '',
      item: {},
    },
  },
  {
    id: 'dataverse_list',
    name: 'List rows',
    type: 'ApiConnection',
    kind: 'V1',
    category: 'action',
    description: 'List rows from a Dataverse table',
    brandColor: '#2E7D32',
    connectorName: 'Dataverse',
    isConnector: true,
    defaultInputs: {
      tableName: '',
    },
  },
  // AI/ML Services
  {
    id: 'openai_chat',
    name: 'Get chat completions',
    type: 'ServiceProvider',
    kind: 'OpenAI',
    category: 'action',
    description: 'Get chat completions from OpenAI GPT models',
    brandColor: '#10A37F',
    connectorName: 'Azure OpenAI',
    isConnector: true,
    defaultInputs: {
      deploymentId: '',
      messages: [],
    },
  },
  {
    id: 'openai_embeddings',
    name: 'Get embeddings',
    type: 'ServiceProvider',
    kind: 'OpenAI',
    category: 'action',
    description: 'Get embeddings from OpenAI',
    brandColor: '#10A37F',
    connectorName: 'Azure OpenAI',
    isConnector: true,
    defaultInputs: {
      deploymentId: '',
      input: '',
    },
  },
  // Function and workflow calls
  {
    id: 'azure_function',
    name: 'Call an Azure function',
    type: 'Function',
    category: 'action',
    description: 'Invoke an Azure Function',
    brandColor: '#FFC107',
    connectorName: 'Azure Functions',
    isConnector: true,
    defaultInputs: {
      functionName: '',
      body: {},
    },
  },
  {
    id: 'invoke_workflow',
    name: 'Invoke a workflow',
    type: 'Workflow',
    category: 'action',
    description: 'Call another Logic App workflow',
    brandColor: '#0078D4',
    defaultInputs: {
      host: {
        workflow: {
          id: '',
        },
      },
      body: {},
    },
  },
];

/**
 * Control flow operations
 */
export const CONTROL_FLOW_OPERATIONS: OperationDefinition[] = [
  {
    id: 'condition',
    name: 'Condition',
    type: 'If',
    category: 'control',
    description: 'Add conditional logic with true/false branches',
    brandColor: '#8C3FBF',
    defaultInputs: {},
  },
  {
    id: 'switch',
    name: 'Switch',
    type: 'Switch',
    category: 'control',
    description: 'Add multi-branch logic based on a value',
    brandColor: '#0078D4',
    defaultInputs: {
      expression: '',
      cases: {},
      default: { actions: {} },
    },
  },
  {
    id: 'foreach',
    name: 'For each',
    type: 'Foreach',
    category: 'control',
    description: 'Loop over an array of items',
    brandColor: '#0E8A7E',
    defaultInputs: {
      foreach: '',
      actions: {},
    },
  },
  {
    id: 'until',
    name: 'Until',
    type: 'Until',
    category: 'control',
    description: 'Loop until a condition is met',
    brandColor: '#0E8A7E',
    defaultInputs: {
      expression: '',
      limit: {
        count: 60,
        timeout: 'PT1H',
      },
      actions: {},
    },
  },
];

/**
 * Agent operations (AI-powered)
 */
export const AGENT_OPERATIONS: OperationDefinition[] = [
  {
    id: 'agent',
    name: 'Agent',
    type: 'Agent',
    category: 'action',
    description: 'Add an AI agent to orchestrate tasks',
    brandColor: '#8B5CF6',
    defaultInputs: {
      model: 'gpt-4o',
      systemPrompt: '',
      tools: {},
    },
  },
];

/**
 * Inline code operations
 */
export const INLINE_CODE_OPERATIONS: OperationDefinition[] = [
  {
    id: 'JavaScriptCode',
    name: 'Execute JavaScript Code',
    type: 'JavaScriptCode',
    category: 'action',
    description: 'Execute JavaScript code inline in your workflow',
    brandColor: '#ba5d00',
    icon: 'https://logicappsv2resources.blob.core.windows.net/icons/inline_code.svg',
    defaultInputs: {
      code: '',
    },
  },
];

/**
 * Get all built-in operations
 */
export const getAllBuiltinOperations = (): OperationDefinition[] => {
  return [...BUILTIN_TRIGGERS, ...BUILTIN_ACTIONS, ...CONTROL_FLOW_OPERATIONS, ...AGENT_OPERATIONS, ...INLINE_CODE_OPERATIONS];
};

/**
 * Get operations by category
 */
export const getOperationsByCategory = (category: OperationCategory): OperationDefinition[] => {
  return getAllBuiltinOperations().filter((op) => op.category === category);
};

/**
 * Find an operation by its ID
 */
export const findOperationById = (id: string): OperationDefinition | undefined => {
  return getAllBuiltinOperations().find((op) => op.id === id);
};

/**
 * Find an operation by its type
 */
export const findOperationByType = (type: string): OperationDefinition | undefined => {
  return getAllBuiltinOperations().find((op) => op.type === type);
};


export interface OperationApi {
  brandColor?: string;
  description?: string;
  displayName: string;
  iconUri: string;
  category?: 'Standard'; // could be consumption too
  id: string;
  isBuiltIn?: boolean;
  isCustomApi?: boolean;
  location?: string;
  name?: string;
  tier?: string;
  type?: string;
  externalDocs?: ExternalDocs;
}

export interface ExternalDocs {
  url: string;
}

export interface Badge {
  name: string;
  description: string;
}


export interface SomeKindOfAzureOperationDiscovery {
  annotation?: {
    status: string;
    family?: string;
    revision?: number;
  };
  capabilities?: string[];
  isNotification?: boolean;
  api: OperationApi;
  description: string;
  operationType?: string;
  operationKind?: string;
  summary: string;
  swaggerOperationId?: string;
  environmentBadge?: Badge;
  isCustomApi?: boolean;
  isChunkingSupported?: boolean;
  pageable: boolean;
  isWebhook: boolean;
  // infoMessage?: InfoMessage;
  externalDocs: ExternalDocs;
  // swaggerExternalDocs?: Swagger.ExternalDocumentation;
  trigger?: string;
  triggerHint?: string;
  visibility?: string;
  // _overrideDefaultDynamicParameterTypes?: Set<DynamicallyAddedParameterType>;
}

export interface BuiltInOperation {
  annotation?: {
    status: string;
  };
  api: OperationApi;
  description: string;
  operationType: 'serviceProvider' | string;
  operationKind?: string;
  summary: string;
  brandColor: string;
  visibility: string;
  iconUri: string;
  trigger?: string;
  capabilities?: string[];
  isCustomApi?: boolean;
}

export type DiscoveryResultTypes = SomeKindOfAzureOperationDiscovery | BuiltInOperation;

export interface DiscoveryOperation<TData extends DiscoveryResultTypes> {
  properties: TData;
  location?: string;
  description?: string;
  icon?: string;
  id: string;
  kind?: string;
  name: string;
  type: string;
}

export type DiscoveryOpArray = DiscoveryOperation<DiscoveryResultTypes>[];